/**
 * Cryptographic utilities for HMAC signing
 * Supports both Node.js and browser environments
 */

/**
 * Convert ArrayBuffer to base64 string
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  // Use btoa if available (browser)
  if (typeof btoa !== "undefined") {
    return btoa(binary);
  }

  // Use Buffer if available (Node.js)
  // @ts-ignore - Buffer is available in Node.js
  if (typeof Buffer !== "undefined") {
    // @ts-ignore
    return Buffer.from(binary, "binary").toString("base64");
  }

  throw new Error("No base64 encoding available");
}

/**
 * Compute SHA256 hash of data and return as base64
 * @param data - Data to hash
 * @returns Base64-encoded SHA256 hash
 */
export async function sha256Base64(data: string): Promise<string> {
  // Use Web Crypto API if available (browser and modern Node.js)
  if (typeof crypto !== "undefined" && crypto.subtle) {
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);
    const hashBuffer = await crypto.subtle.digest("SHA-256", dataBuffer);
    return arrayBufferToBase64(hashBuffer);
  }

  // Fall back to Node.js crypto module
  try {
    // @ts-ignore - dynamic require for Node.js environment
    const nodeCrypto = require("crypto");
    const hash = nodeCrypto.createHash("sha256");
    hash.update(data);
    return hash.digest("base64");
  } catch (error) {
    throw new Error(
      "No crypto implementation available. Please ensure you are running in Node.js or a modern browser."
    );
  }
}

/**
 * Generate HMAC-SHA256 signature for request authentication
 *
 * @param method - HTTP method (GET, POST, etc.)
 * @param path - Request path (e.g., /api/v1/feedback)
 * @param body - Request body (null for GET)
 * @param timestamp - Unix timestamp in seconds
 * @param nonce - Random nonce string
 * @param appSecret - Application secret key
 * @returns Base64-encoded HMAC signature
 */
export async function signRequest(
  method: string,
  path: string,
  body: string | null,
  timestamp: number,
  nonce: string,
  appSecret: string
): Promise<string> {
  // Compute SHA256 hash of body (matching backend implementation)
  const bodyHash = await sha256Base64(body || "");

  // Create string to sign
  const stringToSign = [
    method.toUpperCase(),
    path,
    timestamp.toString(),
    nonce,
    bodyHash,
  ].join("\n");

  // Use Web Crypto API if available (browser and modern Node.js)
  if (typeof crypto !== "undefined" && crypto.subtle) {
    return signWithWebCrypto(stringToSign, appSecret);
  }

  // Fall back to Node.js crypto module
  return signWithNodeCrypto(stringToSign, appSecret);
}

/**
 * Sign using Web Crypto API (browser and modern Node.js)
 */
async function signWithWebCrypto(
  data: string,
  secret: string
): Promise<string> {
  const encoder = new TextEncoder();

  // Import secret key
  const key = await crypto.subtle.importKey(
    "raw",
    encoder.encode(secret),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"]
  );

  // Sign data
  const signature = await crypto.subtle.sign("HMAC", key, encoder.encode(data));

  // Convert to base64
  return arrayBufferToBase64(signature);
}

/**
 * Sign using Node.js crypto module
 */
function signWithNodeCrypto(data: string, secret: string): Promise<string> {
  try {
    // Dynamic import to avoid bundling issues in browser
    // @ts-ignore - dynamic require for Node.js environment
    const crypto = require("crypto");
    const hmac = crypto.createHmac("sha256", secret);
    hmac.update(data);
    return Promise.resolve(hmac.digest("base64"));
  } catch (error) {
    throw new Error(
      "No crypto implementation available. Please ensure you are running in Node.js or a modern browser."
    );
  }
}

/**
 * Generate a random nonce in UUID v4 format
 * @returns Random UUID v4 string
 */
export function generateNonce(): string {
  // Use crypto.randomUUID if available (modern browsers and Node.js 14.17+)
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Use Web Crypto API to generate UUID v4
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);

    // Set version (4) and variant bits according to RFC 4122
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // Version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // Variant 10

    // Format as UUID: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
    const hex = Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(
      12,
      16
    )}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
  }

  // Fall back to Node.js crypto
  try {
    // @ts-ignore - dynamic require for Node.js environment
    const nodeCrypto = require("crypto");
    // Node.js 14.17+ has randomUUID
    if (nodeCrypto.randomUUID) {
      return nodeCrypto.randomUUID();
    }
    // Older Node.js: generate UUID v4 manually
    const bytes = nodeCrypto.randomBytes(16);
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // Version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // Variant 10
    const hex = bytes.toString("hex");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(
      12,
      16
    )}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
  } catch (error) {
    // Last resort: Math.random (not cryptographically secure)
    console.warn(
      "Using Math.random for nonce generation - not cryptographically secure"
    );
    const bytes = Array.from({ length: 16 }, () =>
      Math.floor(Math.random() * 256)
    );
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // Version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // Variant 10
    const hex = bytes.map((b) => b.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(
      12,
      16
    )}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
  }
}

/**
 * Get current Unix timestamp in seconds
 * @returns Current timestamp
 */
export function getCurrentTimestamp(): number {
  return Math.floor(Date.now() / 1000);
}
